import { Box, Link } from '@mui/material';
import strings, { url } from 'constants/index';
import { SimplifiedProposal, SimplifiedTrackingStatus } from 'features/proposal/Proposal.types';
import { simplifiedProposalTableStyles } from './IngestionFixturesTable/IngestionFixturesTable.styles';

export const proposalsUrlMappingByEntityType = {
  ['fixture']: 'fixtures',
  ['player']: 'players',
  ['double']: 'doubles',
  ['team']: 'teams',

  ['contract']: 'contracts',
  ['season']: 'hierarchy/seasons',
  ['competition']: 'hierarchy/competitions',
  ['persons']: 'hierarchy/persons',
  ['organization']: 'hierarchy/organizations',
  ['club']: 'hierarchy/clubs',
  ['conference']: 'hierarchy/groups',
  ['division']: 'hierarchy/groups',
  ['competitorgroups']: 'hierarchy/groups',
  ['round']: 'hierarchy/rounds',
  ['venue']: 'hierarchy/venues',
  ['locality']: 'hierarchy/localities',
  ['seasons competitor']: 'hierarchy/seasons competitor',
};

/** Path segment for `/proposals/proposal/update/{path}/{id}` (no hierarchy/ prefix). */
export const getIngestionUpdateProposalEntityPath = (entityType: string): string => {
  if (entityType === 'sport') {
    return 'sports';
  }

  const createPath = proposalsUrlMappingByEntityType[entityType];
  if (createPath) {
    return createPath.split('/').pop() || entityType;
  }

  return entityType.endsWith('s') ? entityType : `${entityType}s`;
};

export function switchCompetitionTypeForUrl(input: string): string {
  switch (input) {
    case 'TeamsCompetition':
      return 'Teams';
    case 'PlayersCompetition':
      return 'Players';
    case 'DoublePartnershipsCompetition':
      return 'Doubles';
    case 'HorsesCompetition':
      return 'Horse';
    default:
      return input;
  }
}

export const detailsFormatter = (
  simplifiedProposal: SimplifiedProposal,
  showProposalDetails: any,
) => {
  if (!simplifiedProposal) {
    return '';
  }
  return (
    <Box sx={{ display: 'flex' }}>
      <Link
        sx={simplifiedProposalTableStyles.link}
        onClick={showProposalDetails(simplifiedProposal)}
      >
        {simplifiedProposal?.sourceName || ''}
      </Link>
    </Box>
  );
};

export function switchCompetitionTypeForCurationUrl(input: string): string {
  switch (input) {
    case 'TeamsCompetition':
      return 'teams';
    case 'PlayersCompetition':
      return 'players';
    case 'DoublePartnershipsCompetition':
      return 'doubles';
    case 'HorsesCompetition':
      return 'horses';
    default:
      return input;
  }
}

export function mapSingularToPlural(input: string) {
  switch (input) {
    case 'player':
    case 'Player':
      return 'players';
    case 'team':
    case 'Team':
      return 'teams';
    case 'double':
    case 'Double':
      return 'doubles';
    default:
      return input;
  }
}

export function entityTypeToMatchingUrl(input: string): string {
  switch (input) {
    case 'fixture':
      return 'fixtures';
    case 'player':
    case 'Player':
      return 'players';
    case 'team':
    case 'Team':
      return 'teams';
    case 'double':
    case 'Double':
    case 'organization':
    case 'club':
    case 'conference':
    case 'division':
    case 'competitorgroups':
    case 'competition':
    case 'person':
      return 'other';
    default:
      return input;
  }
}

export function EntityTypeToUrl(input: string): string {
  switch (input) {
    case 'organization':
      return url.base_organizations;
    case 'club':
      return url.base_clubs;
    case 'conference':
    case 'division':
    case 'competitorgroups':
      return url.base_groups;
    case 'competition':
      return url.base_competitions;
    case 'season':
      return url.base_seasons;
    case 'round':
      return url.base_rounds;
    case 'venue':
      return url.base_venues;
    case 'players':
    case 'Player':
      return url.base_players;
    case 'team':
    case 'teams':
    case 'Team':
      return url.base_teams;
    case 'sport':
      return url.base_sports;
    case 'double':
    case 'doubles':
      return url.base_doubles;
    default:
      return input;
  }
}

export const SimplifiedTrackingStatusDescriptions: {
  [_ in SimplifiedTrackingStatus]: { name: string; description: string };
} = {
  [SimplifiedTrackingStatus.Accepted]: {
    name: strings.ingestion_Accepted,
    description: strings.ingestion_ProcessingNew,
  },
  [SimplifiedTrackingStatus.AwaitingMatching]: {
    name: strings.ingestion_AwaitingMatching,
    description: strings.ingestion_RequiresMatching,
  },
  // [SimplifiedTrackingStatus.UnmatchedAndProposed]: {
  //   name: strings.ingestion_UnmatchedAndProposed,
  //   description: strings.ingestion_UnmatchedProposalPending,
  // },
  [SimplifiedTrackingStatus.MatchedAndProposed]: {
    name: strings.ingestion_MatchedAndProposed,
    description: strings.ingestion_UpdateWithValidationError,
  },
  // [SimplifiedTrackingStatus.ProposedAndRejected]: {
  //   name: strings.ingestion_ProposedAndRejected,
  //   description: strings.ingestion_ProposedAndRejectedDescription,
  // },
  [SimplifiedTrackingStatus.ProposedAndApproved]: {
    name: strings.ingestion_ProposedAndApproved,
    description: strings.ingestion_ProposedAndApprovedDescription,
  },
  // [SimplifiedTrackingStatus.ProposedAndOutdated]: {
  //   name: strings.ingestion_ProposedAndOutdated,
  //   description: strings.ingestion_Outdated,
  // },
  // [SimplifiedTrackingStatus.InvalidProposal]: {
  //   name: strings.ingestion_InvalidProposal,
  //   description: strings.ingestion_Invalid,
  // },
  [SimplifiedTrackingStatus.Matched]: {
    name: strings.ingestion_Matched,
    description: strings.ingestion_MatchedDescription,
  },
  [SimplifiedTrackingStatus.FailedToProcess]: {
    name: strings.ingestion_FailedToProcess,
    description: strings.ingestion_ProcessingFailed,
  },
  [SimplifiedTrackingStatus.ProposedAndWaitingCuration]: {
    name: strings.ingestion_ProposedAndWaitingCuration,
    description: strings.ingestion_AwaitingCuration,
  },
  // [SimplifiedTrackingStatus.AwaitingMandatoryRelationResolution]: {
  //   name: strings.ingestion_AwaitingMandatoryRelationResolution,
  //   description: strings.ingestion_MissingSeasonsCompetitors,
  // },
  [SimplifiedTrackingStatus.EnrichingProposal]: {
    name: strings.ingestion_EnrichingProposal,
    description: strings.ingestion_EnrichingProposalDescription,
  },
  // [SimplifiedTrackingStatus.Expired]: {
  //   name: strings.ingestion_Expired,
  //   description: strings.ingestion_Expired,
  // },
  [SimplifiedTrackingStatus.Suspended]: {
    name: strings.ingestion_Suspended,
    description: strings.ingestion_Suspended,
  },
  [SimplifiedTrackingStatus.Ignored]: {
    name: strings.ingestion_Ignored,
    description: strings.ingestion_Ignored,
  },
  // [SimplifiedTrackingStatus.Inconsistent]: {
  //   name: strings.ingestion_Inconsistent,
  //   description: strings.ingestion_InconsistentDescription,
  // },
};

export const getSimplifiedTrackingStatusName = (status: SimplifiedTrackingStatus): string =>
  SimplifiedTrackingStatusDescriptions[status].name || strings.not_applicable;

export const getSimplifiedTrackingStatusDescription = (status: SimplifiedTrackingStatus): string =>
  SimplifiedTrackingStatusDescriptions[status]?.description || strings.not_applicable;

export const getSimplifiedTrackingStatusNameByDescription = (description: string): string => {
  for (const status in SimplifiedTrackingStatusDescriptions) {
    if (SimplifiedTrackingStatusDescriptions[status].description === description) {
      return SimplifiedTrackingStatusDescriptions[status].name;
    }
  }

  for (const status in SimplifiedTrackingStatusDescriptions) {
    if (SimplifiedTrackingStatusDescriptions[status].name === description) {
      return description;
    }
  }

  return strings.not_applicable;
};

export const getSimplifiedTrackingStatusDescriptionByName = (name: string): string => {
  for (const status in SimplifiedTrackingStatusDescriptions) {
    if (SimplifiedTrackingStatusDescriptions[status].name === name) {
      return SimplifiedTrackingStatusDescriptions[status].description;
    }
  }

  for (const status in SimplifiedTrackingStatusDescriptions) {
    if (SimplifiedTrackingStatusDescriptions[status].description === name) {
      return name;
    }
  }

  return name;
};

export type PlayerAttributeSource = {
  height?: number | null;
  weight?: number | null;
  yearInUniversity?: string | null;
  sportSpecificRules?: Array<{ name?: string; value?: string | number | null }> | null;
};

export function formatPlayerAttributeSegments(player?: PlayerAttributeSource | null): string[] {
  if (!player) {
    return [];
  }

  const segments: string[] = [];

  if (player.height != null && !Number.isNaN(Number(player.height))) {
    segments.push(`${player.height} cm`);
  }

  if (player.weight != null && !Number.isNaN(Number(player.weight))) {
    segments.push(`${player.weight} kg`);
  }

  if (player.yearInUniversity) {
    segments.push(String(player.yearInUniversity));
  }

  (player.sportSpecificRules || []).forEach(rule => {
    if (rule?.name && rule.value != null && String(rule.value).trim() !== '') {
      segments.push(`${rule.name}: ${rule.value}`);
    }
  });

  return segments;
}
